/**
 * Guarded access to browser storage (SKU-1339).
 *
 * Every `localStorage`/`sessionStorage` operation in the app should go through
 * here. Before this module existed there were 15 direct call sites, each
 * re-implementing (or forgetting) its own try/catch — and the three that
 * mattered most, the `authToken` writes, were among the ones that forgot.
 *
 * ## Why storage operations need guarding at all
 *
 * `setItem` is not a safe call. It throws when:
 *   - the origin is over quota;
 *   - the browser's quota accounting is corrupt (the SKU-1339 incident: Firefox
 *     reported ~2^64 bytes of usage against a 10 GiB ceiling and refused every
 *     write, with 291 GB free on disk and 0.00 MB actually in localStorage);
 *   - the underlying filesystem write fails (`NS_ERROR_FILE_NO_DEVICE_SPACE`);
 *   - site data is blocked by policy or private-browsing mode.
 *
 * Reading is not safe either — with cookies/site-data blocked, merely *touching*
 * `window.localStorage` throws `SecurityError`. That is why the area lookup
 * happens inside the try block rather than at module scope.
 *
 * ## Why `isStorageError` exists
 *
 * The Storage API has no way to express "the disk is full" or "the accounting
 * DB is corrupt", so every underlying fault is translated into the same
 * `QuotaExceededError`. That name therefore points at the application while the
 * real cause may be the OS or the browser profile. Callers that also handle
 * network failures (see `AuthContext.executeTokenRefresh`) need to tell the two
 * apart, because a storage fault is *deterministic* — retrying cannot help,
 * and retrying a token refresh burns a refresh-token rotation every cycle.
 *
 * Per CLAUDE.md Rule 2 these helpers never substitute a default or hide the
 * condition: they report failure upward and let the caller decide.
 */

import { logger } from './logger';

const LOG_CONTEXT = 'safeStorage';

export type StorageArea = 'local' | 'session';

/**
 * Error names that mean "the storage layer refused the operation".
 * `NS_ERROR_*` are Firefox internals; the rest are the standard DOM names.
 */
const STORAGE_ERROR_NAMES = new Set([
  'QuotaExceededError',
  'NS_ERROR_DOM_QUOTA_REACHED',
  'NS_ERROR_FILE_NO_DEVICE_SPACE',
  'SecurityError',
]);

/** Legacy numeric codes: 22 = QUOTA_EXCEEDED_ERR, 1014 = Firefox's quota code. */
const STORAGE_ERROR_CODES = new Set([22, 1014]);

/**
 * Thrown when a value that the app *depends on* could not be persisted.
 *
 * Distinct from the boolean returns below: a failed preference write is
 * survivable and reports `false`, whereas a failed token write means the
 * session cannot exist and must interrupt the flow.
 *
 * Deliberately NOT tagged `isServerUnavailable` — that tag routes errors into
 * the unbounded SKU-1012 retry, which is exactly the wrong response here.
 */
export class TokenPersistenceError extends Error {
  override readonly cause?: unknown;

  constructor(key: string, cause?: unknown) {
    super(`Failed to persist "${key}" to browser storage`);
    this.name = 'TokenPersistenceError';
    this.cause = cause;
    // Required for instanceof to work across the TS->ES5 downlevel boundary.
    Object.setPrototypeOf(this, TokenPersistenceError.prototype);
  }
}

/**
 * Does this error mean the storage layer refused us, as opposed to anything
 * else (network, GraphQL, programmer error)?
 *
 * Accepts `unknown` because it is called from catch blocks that also see
 * Apollo errors and plain objects.
 */
export function isStorageError(error: unknown): boolean {
  if (error === null || typeof error !== 'object') return false;

  const candidate = error as { name?: unknown; code?: unknown; message?: unknown };

  if (typeof candidate.name === 'string' && STORAGE_ERROR_NAMES.has(candidate.name)) {
    return true;
  }
  if (typeof candidate.code === 'number' && STORAGE_ERROR_CODES.has(candidate.code)) {
    return true;
  }
  // Firefox surfaces the device-space failure as a bare Error whose message
  // carries the code — there is no structured field to read.
  if (
    typeof candidate.message === 'string' &&
    candidate.message.includes('NS_ERROR_FILE_NO_DEVICE_SPACE')
  ) {
    return true;
  }

  return false;
}

/**
 * Resolve the storage area. Kept as a function (not a module-level constant)
 * because the property access itself throws when site data is blocked.
 */
function areaOf(area: StorageArea): Storage {
  return area === 'session' ? window.sessionStorage : window.localStorage;
}

function describeError(error: unknown): string {
  if (error instanceof Error) return `${error.name}: ${error.message}`;
  return String(error);
}

/**
 * Result of a read that distinguishes "there is no value" from "we could not
 * look".
 *
 * `safeGet` collapses both into `null`, which is fine for preferences but wrong
 * for anything security-sensitive: treating an unreadable store as "no token"
 * is the read-side twin of the write-side misclassification this module exists
 * to prevent. Callers that would take a *different action* on the two — sending
 * a request unauthenticated, redirecting to login — should use `readStorage`.
 */
export type StorageReadResult =
  | { status: 'ok'; value: string }
  | { status: 'absent' }
  | { status: 'error'; error: unknown };

/**
 * Read a value, preserving *why* there is no value.
 *
 * Prefer this over `safeGet` wherever `null` would trigger an action rather
 * than just a fallback.
 */
export function readStorage(key: string, area: StorageArea = 'local'): StorageReadResult {
  try {
    const value = areaOf(area).getItem(key);
    return value === null ? { status: 'absent' } : { status: 'ok', value };
  } catch (error) {
    logger.warn('Storage read failed', LOG_CONTEXT, {
      key,
      area,
      error: describeError(error),
      isStorageError: isStorageError(error),
    });
    return { status: 'error', error };
  }
}

/**
 * Read a value. Returns null if absent OR if storage is unreadable.
 *
 * Convenience wrapper for callers where both cases genuinely warrant the same
 * fallback (preferences, display defaults). If the two cases should diverge,
 * use `readStorage` instead.
 */
export function safeGet(key: string, area: StorageArea = 'local'): string | null {
  const result = readStorage(key, area);
  return result.status === 'ok' ? result.value : null;
}

/** Write a value. Returns whether the write was accepted. */
export function safeSet(key: string, value: string, area: StorageArea = 'local'): boolean {
  try {
    areaOf(area).setItem(key, value);
    return true;
  } catch (error) {
    logger.warn('Storage write failed', LOG_CONTEXT, {
      key,
      area,
      error: describeError(error),
      isStorageError: isStorageError(error),
    });
    return false;
  }
}

/** Remove a value. Returns whether the removal was accepted. */
export function safeRemove(key: string, area: StorageArea = 'local'): boolean {
  try {
    areaOf(area).removeItem(key);
    return true;
  } catch (error) {
    logger.warn('Storage remove failed', LOG_CONTEXT, { key, area, error: describeError(error) });
    return false;
  }
}

/**
 * Write a value and confirm it reads back.
 *
 * The read-back is not redundant with `safeSet`'s try/catch: a storage layer
 * can accept the call and silently drop the value (private-browsing shims and
 * some quota-pressure paths do exactly this). For anything the app's
 * correctness depends on — the auth token above all — "no exception" is not
 * sufficient evidence that the write landed.
 */
export function verifiedSet(key: string, value: string, area: StorageArea = 'local'): boolean {
  if (!safeSet(key, value, area)) return false;

  const readBack = safeGet(key, area);
  if (readBack !== value) {
    logger.error('Storage write did not persist (read-back mismatch)', LOG_CONTEXT, {
      key,
      area,
      wrote: value.length,
      readBack: readBack === null ? null : readBack.length,
    });
    return false;
  }

  return true;
}
